import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { DollarSign, Truck, Clock, MessageCircle } from "lucide-react";
import { BeforeAfter } from "./BeforeAfter";
import heroJpg from "@/assets/hero-garage.jpg";
import heroWebp from "@/assets/hero-garage.webp";
import heroWebpSm from "@/assets/hero-garage-sm.webp";

const scrollTo = (id: string) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
const WHATSAPP_URL = "https://api.whatsapp.com/send?phone=15874073024&text=Hi%20Garage%20Kings!%20%F0%9F%91%91%20I%27d%20like%20a%20quote%20for%20a%20cleanout.%20What%20photos%20or%20info%20do%20you%20need%20from%20me%20to%20get%20started%3F";

export const Hero = () => (
  <section id="top" className="relative min-h-screen w-full overflow-hidden bg-ink pt-28">
    <div className="absolute inset-0">
      <picture>
        <source media="(max-width: 640px)" srcSet={heroWebpSm} type="image/webp" />
        <source srcSet={heroWebp} type="image/webp" />
        <img
          src={heroJpg}
          alt="Premium organized Edmonton garage by Garage Kings YEG"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover opacity-40"
        />
      </picture>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
    </div>
    <div className="container relative z-10 flex flex-col items-center pb-20 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-4 inline-block max-w-full rounded-full border border-gold/40 bg-ink/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-gold backdrop-blur sm:px-4 sm:text-xs sm:tracking-[0.3em]"
      >
        Edmonton • St. Albert • Sherwood Park • Spruce Grove • Leduc
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="font-display text-[2.75rem] leading-[0.95] tracking-wide text-white sm:text-7xl md:text-8xl"
      >
        Edmonton's <span className="text-gradient-gold">Only</span><br />Garage Specialist
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mx-auto mt-6 max-w-2xl text-lg text-white/80 md:text-xl"
      >
        Complete Garage Resets. We buy your gear, haul the junk, and scrub the space.
        <span className="text-gold"> Same-week service available.</span>
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.45 }}
        className="mt-8 flex flex-col items-center gap-3 sm:flex-row"
      >
        <Button asChild size="lg" className="h-14 bg-gold px-8 font-bold uppercase tracking-wider text-ink shadow-gold hover:bg-gold/90 hover:scale-105 transition-transform">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="mr-2 h-5 w-5" /> Get Your Fast Estimate
          </a>
        </Button>
        <Button onClick={() => scrollTo("#how")} variant="outline" size="lg" className="h-14 border-2 border-white bg-transparent px-8 font-bold uppercase tracking-wider text-white hover:bg-white hover:text-ink">
          See How It Works
        </Button>
      </motion.div>

      <div className="mt-10 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
        {[
          { icon: DollarSign, label: "Flat Rate Pricing" },
          { icon: Truck, label: "We Buy Your Stuff" },
          { icon: Clock, label: "Same-Week Service" },
        ].map((b, i) => (
          <motion.div
            key={b.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 + i * 0.1 }}
            className="flex items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/5 px-4 py-3 backdrop-blur"
          >
            <b.icon className="h-5 w-5 text-gold" />
            <span className="text-sm font-semibold text-white">{b.label}</span>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="mt-14 w-full"
      >
        <p className="mb-4 text-sm uppercase tracking-[0.3em] text-white/50">Drag to see the transformation</p>
        <BeforeAfter />
      </motion.div>
    </div>
  </section>
);
